import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import type { ArticleVideo } from '../lib/articleVideo';

export type { ArticleVideo };

export interface CMSArticle {
  id: string;
  title: string;
  slug: string;
  category: string;
  author: { name: string; role: string; avatar: string };
  date: string;
  readTime: number;
  excerpt: string;
  image: string;
  body: string;
  tags: string[];
  status: 'published' | 'draft' | 'scheduled' | 'archived';
  seo: {
    metaTitle: string;
    metaDescription: string;
    focusKeyword: string;
    ogImage: string;
    canonical: string;
    noIndex: boolean;
    /** How-to video metadata (series/episode/streamId/steps/transcript…). */
    video?: ArticleVideo;
    /** Any additional seo keys are preserved verbatim on save. */
    [key: string]: unknown;
  };
  scheduledPublishAt?: string | null;
  viewCount?: number;
  engagementScore?: number;
  createdAt?: string;
  updatedAt?: string;
}

interface ArticlesContextValue {
  articles: CMSArticle[];
  loading: boolean;
  error: string | null;
  addArticle: (article: CMSArticle) => Promise<void>;
  updateArticle: (id: string, updates: Partial<CMSArticle>) => Promise<void>;
  archiveArticle: (id: string) => Promise<void>;
  refreshArticles: () => Promise<void>;
}

/**
 * Optional server-side seed. The prerender build calls `setPreloadedArticles`
 * with the published articles it fetched from Supabase, so server-rendered
 * pages that read `seo.video` (e.g. WatchCard) have the data without a browser
 * fetch. Left null in the browser.
 */
let preloadedArticles: CMSArticle[] | null = null;

export function setPreloadedArticles(list: CMSArticle[]): void {
  preloadedArticles = Array.isArray(list) ? list : null;
}

const ArticlesContext = createContext<ArticlesContextValue | null>(null);

export function toDBRow(article: Partial<CMSArticle> & { id: string }) {
  const row: Record<string, unknown> = {
    id: article.id,
    updated_at: new Date().toISOString(),
  };
  if (article.title !== undefined) row.title = article.title;
  if (article.slug !== undefined) row.slug = article.slug;
  if (article.category !== undefined) row.category = article.category;
  if (article.author !== undefined) row.author = article.author;
  if (article.date !== undefined) row.date = article.date;
  if (article.readTime !== undefined) row.read_time = article.readTime;
  if (article.excerpt !== undefined) row.excerpt = article.excerpt;
  if (article.image !== undefined) row.image = article.image;
  if (article.body !== undefined) row.body = article.body;
  if (article.tags !== undefined) row.tags = article.tags;
  if (article.status !== undefined) row.status = article.status;
  if (article.seo !== undefined) row.seo = article.seo;
  if (article.scheduledPublishAt !== undefined) row.scheduled_publish_at = article.scheduledPublishAt;
  if (article.viewCount !== undefined) row.view_count = article.viewCount;
  if (article.engagementScore !== undefined) row.engagement_score = article.engagementScore;
  return row;
}

function fromDBRow(row: Record<string, unknown>): CMSArticle {
  const author = (row.author as Record<string, string>) || {};
  const seo = (row.seo as Record<string, unknown>) || {};
  return {
    id: row.id as string,
    title: (row.title as string) || '',
    slug: (row.slug as string) || '',
    category: (row.category as string) || 'Industry Insights',
    author: {
      name: author.name || 'Cendien Marketing',
      role: author.role || 'Marketing Team',
      avatar: author.avatar || '',
    },
    date: (row.date as string) || '',
    readTime: (row.read_time as number) || 5,
    excerpt: (row.excerpt as string) || '',
    image: (row.image as string) || '',
    body: (row.body as string) || '',
    tags: (row.tags as string[]) || [],
    status: ((row.status as string) || 'draft') as 'published' | 'draft' | 'scheduled' | 'archived',
    // Preserve EVERY seo key (e.g. video, and any future keys) — only fill
    // defaults for the known ones. This is what stops a save from wiping
    // seo.video / any custom key the editor doesn't explicitly know about.
    seo: {
      ...seo,
      metaTitle: (seo.metaTitle as string) || '',
      metaDescription: (seo.metaDescription as string) || '',
      focusKeyword: (seo.focusKeyword as string) || '',
      ogImage: (seo.ogImage as string) || '',
      canonical: (seo.canonical as string) || '',
      noIndex: Boolean(seo.noIndex),
    } as CMSArticle['seo'],
    scheduledPublishAt: (row.scheduled_publish_at as string) || null,
    viewCount: (row.view_count as number) || 0,
    engagementScore: (row.engagement_score as number) || 0,
    createdAt: (row.created_at as string) || undefined,
    updatedAt: (row.updated_at as string) || undefined,
  };
}

export function ArticlesProvider({ children }: { children: ReactNode }) {
  const [articles, setArticles] = useState<CMSArticle[]>(() => preloadedArticles || []);
  const [loading, setLoading] = useState(!preloadedArticles);
  const [error, setError] = useState<string | null>(null);

  const fetchArticles = useCallback(async () => {
    try {
      const { data, error: fetchError } = await supabase
        .from('articles')
        .select('*')
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;
      setArticles((data || []).map(fromDBRow));
      setError(null);

      // One-time migration from localStorage
      const stored = localStorage.getItem('cms_articles');
      if (stored && data) {
        try {
          const localItems: CMSArticle[] = JSON.parse(stored);
          // Only migrate string-ID articles (created via editor), not numeric mock IDs
          const cmsLocal = localItems.filter(
            (a) => typeof a.id === 'string' && a.id.startsWith('article-') && a.title
          );
          const existingIds = new Set((data || []).map((r) => r.id));
          const toMigrate = cmsLocal.filter((a) => !existingIds.has(a.id));
          if (toMigrate.length > 0) {
            const rows = toMigrate.map((a) => ({
              ...toDBRow(a),
              created_at: new Date().toISOString(),
            }));
            await supabase.from('articles').insert(rows);
            // Re-fetch after migration
            const { data: refreshed } = await supabase
              .from('articles')
              .select('*')
              .order('created_at', { ascending: false });
            if (refreshed) setArticles(refreshed.map(fromDBRow));
          }
        } catch {
          // migration failed silently
        }
        // Remove old localStorage key to avoid re-migration
        localStorage.removeItem('cms_articles');
      }
    } catch (err) {
      setError('Failed to load articles');
      // Fall back to empty list; mock articles handled separately
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchArticles();
  }, [fetchArticles]);

  const addArticle = useCallback(async (article: CMSArticle) => {
    // Optimistic update
    setArticles((prev) => [article, ...prev]);
    try {
      const row = {
        ...toDBRow(article),
        created_at: new Date().toISOString(),
      };
      const { error: insertError } = await supabase.from('articles').insert([row]);
      if (insertError) throw insertError;
    } catch (err) {
      // Revert optimistic update on failure
      setArticles((prev) => prev.filter((a) => a.id !== article.id));
      throw err;
    }
  }, []);

  const updateArticle = useCallback(async (id: string, updates: Partial<CMSArticle>) => {
    // Optimistic update
    setArticles((prev) =>
      prev.map((a) => (a.id === id ? { ...a, ...updates } : a))
    );
    try {
      const row = toDBRow({ id, ...updates });
      const { error: updateError } = await supabase
        .from('articles')
        .update(row)
        .eq('id', id);
      if (updateError) throw updateError;
    } catch (err) {
      // Revert on failure — re-fetch from DB
      fetchArticles();
      throw err;
    }
  }, [fetchArticles]);

  // Never-delete: archiving only flips status to 'archived' — the row is kept.
  const archiveArticle = useCallback(async (id: string) => {
    const prev = articles.find((a) => a.id === id);
    // Optimistic update
    setArticles((prevList) => prevList.map((a) => (a.id === id ? { ...a, status: 'archived' } : a)));
    try {
      const { error: archiveError } = await supabase
        .from('articles')
        .update({ status: 'archived', updated_at: new Date().toISOString() })
        .eq('id', id);
      if (archiveError) throw archiveError;
    } catch (err) {
      // Revert on failure
      if (prev) setArticles((prevList) => prevList.map((a) => (a.id === id ? prev : a)));
      throw err;
    }
  }, [articles]);

  return (
    <ArticlesContext.Provider
      value={{ articles, loading, error, addArticle, updateArticle, archiveArticle, refreshArticles: fetchArticles }}
    >
      {children}
    </ArticlesContext.Provider>
  );
}

export function useArticlesContext() {
  const ctx = useContext(ArticlesContext);
  if (!ctx) throw new Error('useArticlesContext must be used within ArticlesProvider');
  return ctx;
}
